/**
 * NarrationPlayButton — small inline play/pause control that streams the
 * cached ElevenLabs narration for a (chain_id, step_key) pair.
 *
 * Apr 29 2026 follow-up #3 — Lead's brief: "Narration should begain as
 * soon as a page starts. Complete Narration. Not just a section." Now
 * fetches the FULL-chain audio (step_key='full' — origin + change_a +
 * change_b + contradiction + modern_pattern composed end-to-end) and
 * autoplays it on mount. The chain page navigation is itself a user
 * gesture (BEGIN YOUR JOURNEY / pinhole click), so most browsers will
 * honor the autoplay; if blocked, the pill stays visible with a Play
 * label so the user can tap once to start. Cleanup on unmount cancels
 * the audio cleanly. Audio plays in the background while the chiseled
 * prose continues its visual reveal.
 */
import { useEffect, useRef, useState } from 'react';
import { Play, Pause, Volume2 } from 'lucide-react';
import { apiBase } from '../lib/absoluteMediaUrl';

const API_BASE = apiBase();

export default function NarrationPlayButton({ chainId, stepKey = 'full', label = "Listen — narrated by Midnite", autoPlay = true }) {
  const [available, setAvailable] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const audioRef = useRef(null);
  const triedAutoplayRef = useRef(false);

  // HEAD-check the narration endpoint on mount — partial GET avoids
  // downloading the full payload until the user (or autoplay) hits play.
  useEffect(() => {
    if (!chainId) { setAvailable(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/narration/chain/${encodeURIComponent(chainId)}/${encodeURIComponent(stepKey)}`,
          { method: 'GET', headers: { Range: 'bytes=0-0' } },
        );
        if (cancelled) return;
        setAvailable(res.ok || res.status === 206);
      } catch {
        if (!cancelled) setAvailable(false);
      }
    })();
    return () => { cancelled = true; };
  }, [chainId, stepKey]);

  const ensureAudio = () => {
    if (audioRef.current) return audioRef.current;
    const url = `${API_BASE}/api/narration/chain/${encodeURIComponent(chainId)}/${encodeURIComponent(stepKey)}`;
    // Adopt a pre-warmed audio element if PublicLanding kicked one off
    // inside the BEGIN YOUR JOURNEY click handler. Same chain + same
    // endpoint URL means the audio is already streaming; we just
    // attach our event listeners and keep it going. This is how
    // narration auto-plays on /demo despite browser autoplay rules.
    const pending = (typeof window !== 'undefined' && window.__tsogn_pending_narration) || null;
    if (pending && pending.url && pending.url.endsWith(`/api/narration/chain/${encodeURIComponent(chainId)}/${encodeURIComponent(stepKey)}`)) {
      const a = pending.audio;
      audioRef.current = a;
      a.addEventListener('ended', () => {
        setPlaying(false); setProgress(100);
        window.dispatchEvent(new CustomEvent('narration:tick', { detail: { currentTime: a.duration || 0, duration: a.duration || 0, playing: false, ended: true, chainId, stepKey } }));
      });
      a.addEventListener('timeupdate', () => {
        if (a.duration) setProgress((a.currentTime / a.duration) * 100);
        window.dispatchEvent(new CustomEvent('narration:tick', { detail: { currentTime: a.currentTime, duration: a.duration || 0, playing: !a.paused, ended: false, chainId, stepKey } }));
      });
      a.addEventListener('pause', () => {
        window.dispatchEvent(new CustomEvent('narration:tick', { detail: { currentTime: a.currentTime, duration: a.duration || 0, playing: false, ended: false, chainId, stepKey } }));
      });
      // Reflect the playing state if it's already going
      if (!a.paused) setPlaying(true);
      // Clear the global so a re-mount doesn't re-adopt
      try { delete window.__tsogn_pending_narration; } catch { /* noop */ }
      return a;
    }
    const a = new Audio(url);
    a.preload = 'auto';
    a.addEventListener('ended', () => {
      setPlaying(false); setProgress(100);
      window.dispatchEvent(new CustomEvent('narration:tick', { detail: { currentTime: a.duration || 0, duration: a.duration || 0, playing: false, ended: true, chainId, stepKey } }));
    });
    a.addEventListener('timeupdate', () => {
      if (a.duration) setProgress((a.currentTime / a.duration) * 100);
      window.dispatchEvent(new CustomEvent('narration:tick', { detail: { currentTime: a.currentTime, duration: a.duration || 0, playing: !a.paused, ended: false, chainId, stepKey } }));
    });
    a.addEventListener('pause', () => {
      window.dispatchEvent(new CustomEvent('narration:tick', { detail: { currentTime: a.currentTime, duration: a.duration || 0, playing: false, ended: false, chainId, stepKey } }));
    });
    audioRef.current = a;
    return a;
  };

  const toggle = () => {
    const a = ensureAudio();
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play().then(() => { setPlaying(true); setAutoplayBlocked(false); }).catch((e) => {
        // eslint-disable-next-line no-console
        console.warn('[narration] play blocked:', e);
        setPlaying(false);
        setAutoplayBlocked(true);
      });
    }
  };

  // Auto-play the moment the audio is confirmed available — only fires
  // once per mount. Browser autoplay policy may block; if it does we
  // surface a Play pill and the user can tap. Coming-from-PublicLanding
  // BEGIN YOUR JOURNEY click usually counts as a fresh user gesture so
  // autoplay is honored on Chrome / Safari / Firefox.
  useEffect(() => {
    if (!autoPlay || available !== true || triedAutoplayRef.current) return;
    triedAutoplayRef.current = true;
    toggle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available, autoPlay]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  }, []);

  if (available === null) {
    return (
      <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 text-white/40 font-mono text-xs" data-testid="narration-checking">
        <Volume2 size={12} className="opacity-60" />
        <span className="tracking-wider uppercase">Loading narration…</span>
      </div>
    );
  }
  if (available === false) return null;

  return (
    <div className="mb-5">
      <button
        onClick={toggle}
        data-testid="narration-play"
        data-narration-playing={playing ? '1' : '0'}
        data-narration-autoplay-blocked={autoplayBlocked ? '1' : '0'}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4A84A] bg-[#D4A84A]/12 text-[#D4A84A] font-mono text-xs tracking-widest uppercase hover:bg-[#D4A84A]/22 transition-colors"
      >
        {playing ? <Pause size={13} /> : <Play size={13} />}
        {playing ? 'Pause' : autoplayBlocked ? 'Tap to listen — Midnite' : label}
      </button>
      {/* Progress bar removed Apr 30 2026 per Lead — the thin gold strip
          read as a "blink cursor" under the pause button on the chain
          pages. The audio element still tracks progress internally for
          autoplay handoff; it just isn't surfaced visually anymore. */}
    </div>
  );
}
